import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import SendMessageModal from '../components/SendMessageModal';
import CandidateProfileModal from '../components/CandidateProfileModal';
import {
    Search, Building2, Briefcase, ChevronDown, Loader2,
    Users, MapPin, ExternalLink, MessageCircle,
    Mail, Send, UserCheck
} from 'lucide-react';
import {
    listCompanies, listJobs, findCandidatesForJob, getJobApplicants,
    JobSummary, CandidateMatchResult, ApplicantInfo
} from '../services/jobMatchingApi';
import { normalizeMatchScore } from '../../shared/utils/apiClient';

type CandidateTab = 'applied' | 'platform' | 'imported' | 'imported_se';

const ManageJobs: React.FC = () => {
    // Step 1: Company selection
    const [companies, setCompanies] = useState<string[]>([]);
    const [companySearch, setCompanySearch] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    // Step 2: Job selection
    const [jobs, setJobs] = useState<JobSummary[]>([]);
    const [selectedJob, setSelectedJob] = useState<JobSummary | null>(null);
    const [loadingJobs, setLoadingJobs] = useState(false);

    // Step 3: Candidates
    const [candidates, setCandidates] = useState<CandidateMatchResult[]>([]);
    const [applicants, setApplicants] = useState<ApplicantInfo[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidateError, setCandidateError] = useState('');
    const [activeTab, setActiveTab] = useState<CandidateTab>('platform');

    // Send Message Modal
    const [modalCandidate, setModalCandidate] = useState<CandidateMatchResult | null>(null);
    const [modalCandidates, setModalCandidates] = useState<CandidateMatchResult[] | undefined>(undefined);
    const [modalMode, setModalMode] = useState<'david' | 'email' | 'company'>('david');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Bulk selection state (Platform tab only — selects candidates to recommend
    // together to the job's owning company in a single David message).
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Reset bulk selection when the job, tab, or candidate list changes
    useEffect(() => {
        setSelectedIds(new Set());
    }, [selectedJob?.id, activeTab, candidates.length]);

    // Profile Modal
    const [profileCandidate, setProfileCandidate] = useState<CandidateMatchResult | null>(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    // Load companies on mount
    useEffect(() => {
        loadCompanies();
    }, []);

    const loadCompanies = async (search?: string) => {
        setLoadingCompanies(true);
        try {
            const result = await listCompanies(search);
            setCompanies(result);
        } catch (err) {
            console.error('Failed to load companies:', err);
        } finally {
            setLoadingCompanies(false);
        }
    };

    // Filter companies by search input
    const filteredCompanies = useMemo(() => {
        if (!companySearch) return companies;
        const lower = companySearch.toLowerCase();
        return companies.filter(c => c.toLowerCase().includes(lower));
    }, [companies, companySearch]);

    // Load jobs when company is selected
    useEffect(() => {
        if (selectedCompany) {
            loadJobs(selectedCompany);
        } else {
            setJobs([]);
            setSelectedJob(null);
        }
    }, [selectedCompany]);

    const loadJobs = async (company: string) => {
        setLoadingJobs(true);
        setSelectedJob(null);
        setCandidates([]);
        try {
            const result = await listJobs(company);
            setJobs(result);
        } catch (err) {
            console.error('Failed to load jobs:', err);
        } finally {
            setLoadingJobs(false);
        }
    };

    // Load candidates when job is selected
    useEffect(() => {
        if (selectedJob) {
            loadCandidates(selectedJob.id);
        } else {
            setCandidates([]);
            setApplicants([]);
        }
    }, [selectedJob]);

    const loadCandidates = async (jobId: string) => {
        setLoadingCandidates(true);
        setCandidateError('');
        try {
            const [matchResult, applicantResult] = await Promise.all([
                findCandidatesForJob(jobId),
                getJobApplicants(jobId),
            ]);
            setCandidates(matchResult.matches || []);
            setApplicants(applicantResult || []);
        } catch (err: any) {
            setCandidateError(err.message || 'Failed to find candidates');
        } finally {
            setLoadingCandidates(false);
        }
    };

    // Build set of applied candidate supabase IDs
    const appliedIds = useMemo(() => {
        return new Set(applicants.map(a => a.candidateId));
    }, [applicants]);

    // Split candidates into 4 groups.
    // Phase 2C introduced two imported source tags — 'imported' for
    // m_imp_candidates_all_roles and 'imported_se' for
    // m_imp_candidates_software_engineering. Before this split the SE
    // candidates fell through to the 'platform' tab and made it look like
    // m_candidates held candidates it doesn't actually contain.
    const { appliedCandidates, platformCandidates, importedCandidates, importedSECandidates } = useMemo(() => {
        const applied: CandidateMatchResult[] = [];
        const platform: CandidateMatchResult[] = [];
        const imported: CandidateMatchResult[] = [];
        const importedSE: CandidateMatchResult[] = [];

        for (const c of candidates) {
            if (c.source === 'imported_se') {
                importedSE.push(c);
            } else if (c.source === 'imported') {
                imported.push(c);
            } else if (appliedIds.has(c.candidate.supabaseUserId)) {
                applied.push(c);
            } else {
                // source unset or explicitly 'platform' — from m_candidates
                platform.push(c);
            }
        }

        return {
            appliedCandidates: applied,
            platformCandidates: platform,
            importedCandidates: imported,
            importedSECandidates: importedSE,
        };
    }, [candidates, appliedIds]);

    const currentCandidates = activeTab === 'applied' ? appliedCandidates
        : activeTab === 'platform' ? platformCandidates
        : activeTab === 'imported' ? importedCandidates
        : importedSECandidates;

    // Per-row action. Mode depends on which tab the candidate came from:
    //   Platform → recommend this one candidate to the company owner (David)
    //   Imported / Imported SE → email the candidate about the job match
    //   Applied → legacy 'Share with Company' (unchanged)
    const handleAction = (candidate: CandidateMatchResult) => {
        setModalCandidate(candidate);
        setModalCandidates(undefined); // clear any prior bulk selection
        if (activeTab === 'platform') {
            setModalMode('company');
        } else if (activeTab === 'imported' || activeTab === 'imported_se') {
            setModalMode('email');
        } else {
            // applied tab — legacy
            setModalMode('company');
        }
        setIsModalOpen(true);
    };

    // Bulk-send handler — used only on the Platform tab. Collects every
    // currently-checked candidate and opens the modal in company mode with
    // the full list, so they're bundled into a single David message.
    const handleBulkRecommend = () => {
        const picks = platformCandidates.filter((c) => selectedIds.has(c.candidate.id));
        if (picks.length === 0) return;
        setModalCandidate(picks[0]);
        setModalCandidates(picks);
        setModalMode('company');
        setIsModalOpen(true);
    };

    const toggleSelection = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAllPlatform = () => {
        if (selectedIds.size === platformCandidates.length && platformCandidates.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(platformCandidates.map((c) => c.candidate.id)));
        }
    };

    const getMatchPercent = (vectorSimilarity: number, qwenScore: number, softCriteriaScore: number) =>
        Math.round(normalizeMatchScore(vectorSimilarity, qwenScore, softCriteriaScore) * 100);

    return (
        <AdminLayout showHeader headerTitle="Find Candidates" headerSubtitle="Search for matching candidates by company and job">
            <div className="space-y-6">
                {/* Step 1 + 2: Company & Job Selection */}
                <div className="bg-white rounded-2xl p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Company Search */}
                        <div className="relative">
                            <label className="block text-sm font-medium text-[#0B111E] mb-2">
                                <Building2 size={14} className="inline mr-1.5 -mt-0.5" />
                                Company
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={selectedCompany || companySearch}
                                    onChange={(e) => {
                                        setCompanySearch(e.target.value);
                                        setSelectedCompany('');
                                        setShowCompanyDropdown(true);
                                    }}
                                    onFocus={() => setShowCompanyDropdown(true)}
                                    placeholder="Search company..."
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>

                            {showCompanyDropdown && filteredCompanies.length > 0 && !selectedCompany && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                    {filteredCompanies.map((company) => (
                                        <button
                                            key={company}
                                            onClick={() => {
                                                setSelectedCompany(company);
                                                setCompanySearch('');
                                                setShowCompanyDropdown(false);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#E3F2FD] transition-colors first:rounded-t-xl last:rounded-b-xl"
                                        >
                                            {company}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {loadingCompanies && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm text-gray-500">
                                    <Loader2 size={14} className="inline animate-spin mr-2" />Loading...
                                </div>
                            )}
                        </div>

                        {/* Job Select */}
                        <div>
                            <label className="block text-sm font-medium text-[#0B111E] mb-2">
                                <Briefcase size={14} className="inline mr-1.5 -mt-0.5" />
                                Job Position
                            </label>
                            <div className="relative">
                                <select
                                    value={selectedJob?.id || ''}
                                    onChange={(e) => {
                                        const job = jobs.find(j => j.id === e.target.value);
                                        setSelectedJob(job || null);
                                    }}
                                    disabled={!selectedCompany || loadingJobs}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] disabled:bg-gray-50 disabled:text-gray-400"
                                >
                                    <option value="">
                                        {loadingJobs ? 'Loading jobs...' : !selectedCompany ? 'Select a company first' : `Select job (${jobs.length} available)`}
                                    </option>
                                    {jobs.map((job) => (
                                        <option key={job.id} value={job.id}>
                                            {job.title} {job.location ? `· ${job.location}` : ''}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selected Job Card */}
                {selectedJob && (
                    <div className="bg-white rounded-2xl p-5 border border-gray-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#015FA6] to-[#0186E0] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {selectedJob.companyName?.charAt(0)?.toUpperCase() || 'J'}
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-[#0B111E]">{selectedJob.title}</h3>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-[#757575]">
                                        <span className="inline-flex items-center gap-1">
                                            <Building2 size={12} />
                                            {selectedJob.companyName}
                                        </span>
                                        {selectedJob.location && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin size={12} />
                                                {selectedJob.location}
                                            </span>
                                        )}
                                        {selectedJob.employmentType && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#E3F2FD] text-[#015FA6] font-medium">
                                                {selectedJob.employmentType}
                                            </span>
                                        )}
                                        {selectedJob.experienceLevel && (
                                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-[#515151] font-medium">
                                                {selectedJob.experienceLevel}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!loadingCandidates && candidates.length > 0 && (
                                <div className="text-right flex-shrink-0">
                                    <div className="text-2xl font-bold text-[#015FA6]">{candidates.length}</div>
                                    <div className="text-[10px] text-[#757575] uppercase tracking-wider">matches</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3: Candidate Results */}
                {selectedJob && (
                    <div className="bg-white rounded-2xl">
                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 px-6">
                            {([
                                { key: 'applied' as CandidateTab, label: 'Applied', count: appliedCandidates.length, icon: UserCheck, color: 'text-emerald-600' },
                                { key: 'platform' as CandidateTab, label: 'Platform', count: platformCandidates.length, icon: Users, color: 'text-[#015FA6]' },
                                { key: 'imported' as CandidateTab, label: 'Imported', count: importedCandidates.length, icon: ExternalLink, color: 'text-amber-600' },
                                { key: 'imported_se' as CandidateTab, label: 'Imported · Software Eng', count: importedSECandidates.length, icon: ExternalLink, color: 'text-purple-600' },
                            ]).map(({ key, label, count, icon: Icon, color }) => (
                                <button
                                    key={key}
                                    onClick={() => setActiveTab(key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                        activeTab === key
                                            ? `${color} border-current`
                                            : 'text-[#757575] border-transparent hover:text-[#0B111E]'
                                    }`}
                                >
                                    <Icon size={16} />
                                    {label}
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                                        activeTab === key ? 'bg-current/10' : 'bg-gray-100'
                                    }`}>
                                        {count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Bulk action bar — only on Platform tab */}
                        {activeTab === 'platform' && platformCandidates.length > 0 && !loadingCandidates && (
                            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-100">
                                <label className="flex items-center gap-2 text-xs text-[#757575] cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === platformCandidates.length && platformCandidates.length > 0}
                                        onChange={toggleSelectAllPlatform}
                                        className="w-4 h-4 rounded border-gray-300 text-[#015FA6] focus:ring-[#015FA6]"
                                    />
                                    <span>
                                        {selectedIds.size > 0
                                            ? `${selectedIds.size} selected`
                                            : 'Select all to bulk recommend'}
                                    </span>
                                </label>
                                {selectedIds.size > 0 && (
                                    <button
                                        onClick={handleBulkRecommend}
                                        className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                                    >
                                        <Send size={14} />
                                        Recommend {selectedIds.size} to {selectedJob.companyName}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Loading / Error / Results */}
                        <div className="p-6">
                            {loadingCandidates ? (
                                <div className="text-center py-12">
                                    <Loader2 size={32} className="mx-auto animate-spin text-[#015FA6] mb-3" />
                                    <p className="text-sm text-[#757575]">Finding matching candidates...</p>
                                </div>
                            ) : candidateError ? (
                                <div className="text-center py-12">
                                    <p className="text-sm text-red-500">{candidateError}</p>
                                </div>
                            ) : currentCandidates.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users size={32} className="mx-auto text-gray-300 mb-3" />
                                    <p className="text-sm text-[#757575]">
                                        {activeTab === 'applied' ? 'No candidates have applied to this job yet.'
                                            : activeTab === 'imported' ? 'No matching imported candidates found.'
                                            : activeTab === 'imported_se' ? 'No matching imported SE candidates found.'
                                            : 'No matching platform candidates found.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {currentCandidates.map((match) => (
                                        <CandidateRow
                                            key={match.candidate.id}
                                            match={match}
                                            matchPercent={getMatchPercent(match.vectorSimilarity, match.qwenScore, match.softCriteriaScore)}
                                            tab={activeTab}
                                            selected={activeTab === 'platform' && selectedIds.has(match.candidate.id)}
                                            onToggleSelect={
                                                activeTab === 'platform'
                                                    ? () => toggleSelection(match.candidate.id)
                                                    : undefined
                                            }
                                            onAction={() => handleAction(match)}
                                            onViewProfile={() => { setProfileCandidate(match); setIsProfileOpen(true); }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Empty state when no job selected */}
                {!selectedJob && (
                    <div className="bg-white rounded-2xl p-8">
                        <div className="text-center py-8">
                            <Search size={40} className="mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-semibold text-[#0B111E] mb-2">Select a Company & Job</h3>
                            <p className="text-sm text-[#757575]">
                                Choose a company and job position above to find matching candidates.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <SendMessageModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                candidate={modalCandidate}
                candidates={modalCandidates}
                job={selectedJob}
                mode={modalMode}
                onSuccess={() => {}}
            />

            <CandidateProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                match={profileCandidate}
            />
        </AdminLayout>
    );
};

// ── Candidate Row Component ──

interface CandidateRowProps {
    match: CandidateMatchResult;
    matchPercent: number;
    tab: CandidateTab;
    /** Whether this row is currently selected for bulk actions (Platform tab). */
    selected?: boolean;
    /** Toggle bulk selection. Presence of this prop shows the checkbox. */
    onToggleSelect?: () => void;
    onAction: () => void;
    onViewProfile: () => void;
}

const CandidateRow: React.FC<CandidateRowProps> = ({ match, matchPercent, tab, selected, onToggleSelect, onAction, onViewProfile }) => {
    const c = match.candidate;
    const name = c.name || 'Unknown';
    const title = c.allJobTitles?.[0] || '';
    const location = c.locations?.[0] || '';
    const skills = c.skills?.slice(0, 5) || [];

    const actionConfig: Record<CandidateTab, { icon: typeof Send; label: string; className: string }> = {
        applied: { icon: Send, label: 'Share with Company', className: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
        // Platform candidates are on Hush — David recommends them to the
        // company owner of the selected job.
        platform: { icon: Send, label: 'Recommend to Company', className: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
        // Imported candidates are off-platform — we reach out via email to
        // tell them about a matching job on Hush.
        imported: { icon: Mail, label: 'Email about this job', className: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
        imported_se: { icon: Mail, label: 'Email about this job', className: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
    };

    const action = actionConfig[tab];
    const ActionIcon = action.icon;

    return (
        <div className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${selected ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'}`}>
            {/* Bulk-select checkbox (Platform tab only) */}
            {onToggleSelect && (
                <input
                    type="checkbox"
                    checked={!!selected}
                    onChange={onToggleSelect}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                />
            )}

            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#015FA6] to-[#0186E0] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {name.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={onViewProfile} className="text-sm font-semibold text-[#015FA6] hover:underline truncate">{name}</button>
                    {title && <span className="text-xs text-[#757575]">&middot; {title}</span>}
                    {location && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-[#757575]">
                            <MapPin size={10} />
                            {location}
                        </span>
                    )}
                    {c.linkedinUrl && (
                        <a
                            href={c.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-0.5 text-xs text-[#015FA6] hover:underline"
                        >
                            <ExternalLink size={10} />
                            LinkedIn
                        </a>
                    )}
                </div>
                {skills.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {skills.map((skill, i) => (
                            <span key={i} className="px-2 py-0.5 text-[11px] bg-gray-50 text-[#757575] rounded-full border border-gray-100">
                                {skill}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Match Score */}
            <div className="flex-shrink-0 text-right mr-2">
                <div className={`text-lg font-bold ${matchPercent >= 70 ? 'text-emerald-600' : matchPercent >= 40 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {matchPercent}%
                </div>
                <div className="text-[10px] text-[#757575]">match</div>
            </div>

            {/* Action */}
            <button
                onClick={onAction}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors flex-shrink-0 ${action.className}`}
            >
                <ActionIcon size={14} />
                {action.label}
            </button>
        </div>
    );
};

export default ManageJobs;
